// Native browser speech recognition wrapper
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface VoiceCommand {
  phrase: string;
  callback: () => void;
}

export interface VoiceRecognitionCallbacks {
  onStart?: () => void;
  onEnd?: () => void;
  onResult?: (transcript: string, isFinal: boolean) => void;
  onCommandMatch?: (command: string) => void;
  onError?: (error: string) => void;
  onSpeechDetection?: (isDetecting: boolean) => void;
}

export class VoiceRecognition {
  private recognition: any = null;
  private commands: VoiceCommand[] = [];
  private isListening: boolean = false;
  private callbacks: VoiceRecognitionCallbacks = {};
  private lastMatchedCommand: string | null = null;

  constructor(callbacks?: VoiceRecognitionCallbacks) {
    this.callbacks = callbacks || {};
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      if (this.callbacks.onStart) {
        this.callbacks.onStart();
      }
    };

    recognition.onspeechstart = () => {
      console.log('Speech detected');
      if (this.callbacks.onSpeechDetection) {
        this.callbacks.onSpeechDetection(true);
      }
    };

    recognition.onspeechend = () => {
      console.log('Speech ended');
      if (this.callbacks.onSpeechDetection) {
        this.callbacks.onSpeechDetection(false);
      }
    };

    recognition.onresult = (event: any) => {
      // Process all results from the last result index
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript.toLowerCase().trim();
        const isFinal = result.isFinal;

        console.log(isFinal ? 'Final:' : 'Interim:', transcript);

        if (this.callbacks.onResult) {
          this.callbacks.onResult(transcript, isFinal);
        }

        // Match commands on both interim and final results for immediate response
        this.handleCommand(transcript, isFinal);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);

      if (this.callbacks.onError) {
        this.callbacks.onError(event.error);
      }
    };

    recognition.onend = () => {
      if (this.callbacks.onEnd) {
        this.callbacks.onEnd();
      }

      // Auto-restart if we were listening
      if (this.isListening) {
        console.log('Recognition ended, restarting...');
        recognition.start();
      }
    };

    this.recognition = recognition;
  }

  addCommand(phrase: string, callback: () => void): void {
    this.commands.push({ phrase: phrase.toLowerCase(), callback });
  }

  removeCommand(phrase: string): void {
    this.commands = this.commands.filter(cmd => cmd.phrase !== phrase.toLowerCase());
  }

  private handleCommand(transcript: string, isFinal: boolean): void {
    for (const command of this.commands) {
      if (transcript.includes(command.phrase)) {
        // Only trigger the command once per utterance (avoid duplicate triggers)
        if (this.lastMatchedCommand !== command.phrase) {
          console.log(`Command matched: "${command.phrase}"`);

          if (this.callbacks.onCommandMatch) {
            this.callbacks.onCommandMatch(command.phrase);
          }

          command.callback();
          this.lastMatchedCommand = command.phrase;
        }
        return;
      }
    }

    // Reset last matched command when we get a final result with no match
    if (isFinal) {
      this.lastMatchedCommand = null;
      console.log(`No command matched for: "${transcript}"`);
    }
  }

  start(): void {
    if (!this.recognition) {
      console.warn('Speech recognition not available');
      return;
    }

    if (this.isListening) {
      console.log('Already listening');
      return;
    }

    this.isListening = true;
    this.recognition.start();
    console.log('Voice recognition started');
  }

  stop(): void {
    if (!this.recognition) return;

    this.isListening = false;
    this.recognition.stop();
    console.log('Voice recognition stopped');
  }

  isSupported(): boolean {
    return this.recognition !== null;
  }
}
